/**
 * Server-safe grid-spec lookup for course-card variants.
 *
 * <p>The card variants are {@code 'use client'} modules; React Server
 * Components cannot dot into client-module exports, so a server-rendered
 * row can't read {@code Card.gridSpec}. This file is the server path: a
 * plain data table keyed by variant name, no React imports, safe to
 * import from any server component.
 *
 * <p>Keep these in sync with the static {@code .gridSpec} on each
 * variant file.
 */

/** Grid-spec shape. The variant decides its own layout: */
export type CardGridSpec =
  | { kind: 'grid'; minCardWidth: number; gap?: string }
  | { kind: 'rail'; cardWidth: string; gap?: string }
  | { kind: 'list'; gap?: string };

type ServerSafeVariant =
  | 'default'
  | 'compact'
  | 'wide'
  | 'minimal'
  | 'cinematic'
  | 'marketplace'
  | 'academic'
  | 'udemy'
  | 'classicHigh'
  | 'classicNetflixZoom'
  | 'borderless';

const SPECS: Record<ServerSafeVariant, CardGridSpec> = {
  default: { kind: 'grid', minCardWidth: 180, gap: 'clamp(12px, 2vw, 16px)' },
  compact: { kind: 'grid', minCardWidth: 150, gap: 'clamp(10px, 1.5vw, 14px)' },
  wide: { kind: 'grid', minCardWidth: 420, gap: 'clamp(14px, 2vw, 20px)' },
  minimal: { kind: 'list' },
  cinematic: {
    kind: 'rail',
    cardWidth: 'clamp(220px, 35vw, 320px)',
    gap: 'clamp(10px, 1.5vw, 16px)',
  },
  marketplace: { kind: 'grid', minCardWidth: 200, gap: 'clamp(12px, 2vw, 16px)' },
  academic: { kind: 'grid', minCardWidth: 200, gap: 'clamp(14px, 2vw, 18px)' },
  udemy: { kind: 'grid', minCardWidth: 180, gap: 'clamp(12px, 2vw, 16px)' },
  classicHigh: { kind: 'grid', minCardWidth: 180, gap: 'clamp(12px, 2vw, 16px)' },
  classicNetflixZoom: { kind: 'grid', minCardWidth: 220, gap: 'clamp(20px, 3vw, 34px)' },
  borderless: { kind: 'grid', minCardWidth: 200, gap: 'clamp(12px, 2vw, 16px)' },
};

export const DEFAULT_GRID_SPEC: CardGridSpec = {
  kind: 'grid',
  minCardWidth: 180,
  gap: 'clamp(12px, 2vw, 16px)',
};

/** Fallback used by {@code 'custom'} (theme-primitive variant — its
 *  spec lives on a client component the server can't reach) and for
 *  unknown variant names. */
export const SERVER_SAFE_DEFAULT_GRID_SPEC: CardGridSpec = DEFAULT_GRID_SPEC;

/** Get the grid spec for a variant id without touching any client
 *  module. Returns the default spec for unknown or {@code 'custom'}. */
export function getGridSpec(cardStyle: string | undefined | null): CardGridSpec {
  if (!cardStyle) return SERVER_SAFE_DEFAULT_GRID_SPEC;
  const spec = SPECS[cardStyle as ServerSafeVariant];
  return spec ?? SERVER_SAFE_DEFAULT_GRID_SPEC;
}
